import { QueryClient } from "@tanstack/react-query";

import { BidEntityType as EntityType } from "../../../../../types/graphQLEnums";
import { NoticeDetails } from "../../../notices/useNotice";
import { NoticesDto } from "../../../notices/useNotices";
import { updateCachedNotices } from "../../../notices/utils";
/**
 * On a given entity, either removes or adds the bid ID and name from it's attached bids object
 * @param param0
 */
export function updateBidEntityQuery({
  queryClient,
  entityId,
  entityType,
  bidName,
  bidId,
  isAdding,
}: {
  queryClient: QueryClient;
  entityId: string;
  entityType: EntityType;
  bidName: string;
  bidId: string;
  isAdding: boolean;
}) {
  // If the entity type is a framework, organisation, custom_organisation or a buyer activity report, there are no entity queries to
  // update currently that have bid as a ResolvedField
  if (
    [
      EntityType.Buyer,
      EntityType.Supplier,
      EntityType.Organisation,
      EntityType.CustomOrganisation,
      EntityType.BuyerActivityReport,
      EntityType.Framework,
      EntityType.Document,
      EntityType.Contact,
    ].includes(entityType)
  ) {
    return;
  }

  switch (entityType) {
    case EntityType.Notice: {
      updateCachedNotices({
        queryClient,
        lookup: { type: "procurementStageId", id: entityId },
        updateNoticesRow: (notice) => updateNoticeBids({ bidId, bidName, isAdding, notice }),
        updateNoticeQuery: (notice) => updateNoticeBids({ bidId, bidName, isAdding, notice }),
      });
      break;
    }

    default:
      throw new Error(`Unhandled bid entity type: ${entityType}`);
  }
}

/**
 * Updates the bids field for a notice object in the query cache
 * @param queryClient
 * @param queryKey
 * @param bidIds
 * @param updater
 */
function updateNoticeBids<NoticeType extends NoticesDto | NoticeDetails>({
  bidId,
  bidName,
  isAdding,
  notice,
}: {
  bidId: string;
  bidName: string;
  isAdding: boolean;
  notice: NoticeType;
}): NoticeType {
  if (!isAdding) {
    return {
      ...notice,
      bid: undefined,
    };
  }

  // If adding to a bid, only add if the bid does not already exist on the notice
  const bidExists = !!notice.bid && notice.bid.id === bidId;

  return {
    ...notice,
    bid: bidExists ? notice.bid : { id: bidId, name: bidName },
  };
}
